// Funciones tipadas para cada ruta de docs/CONTRATO.md §3.
import { solicitar } from './http';
import type {
  CambioCaso,
  RespuestaCierre,
  RespuestaDecision,
  RespuestaResolucion,
  Caso,
  CasoDetalle,
  CasoNegocio,
  CasoResumen,
  CatalogoResumen,
  CatalogoVersion,
  ClienteBusqueda,
  Excepcion,
  EstadoExcepcion,
  FiltroCasos,
  Indicadores,
  ListaCasos,
  MargenReal,
  AreaMargen,
  NoOferta,
  NuevaAprobacion,
  NuevaOferta,
  NuevaVersionCatalogo,
  NuevoCaso,
  NuevoResultado,
  Oferta,
  ResolucionExcepcion,
  ResultadoMotor,
  Salud,
  SpecCaso,
  TipoCatalogo,
  Usuario,
  VerificacionOferta,
  Archivo,
  Clasificacion,
  Comentario,
  ItemActividad,
  ListaNotificaciones,
  MiTrabajo,
  ResultadoBusqueda,
  UsuarioDirectorio,
  ActividadPreventa,
  Brecha,
  BrechaAgregada,
  Calificacion,
  Equipo,
  Coach,
  Evaluacion,
  HitoPlan,
  ItemBiblioteca,
  Reunion,
  SaludCaso,
  SaludEntrada,
  Solicitud,
  ValorAgregado,
  ValorMapaItem,
} from './tipos';

const enc = encodeURIComponent;

/** Las listas llegan como `{items, siguiente_cursor}` (se tolera un arreglo simple). */
function normalizarLista<T>(r: unknown): { items: T[]; siguiente_cursor: string | null } {
  if (Array.isArray(r)) return { items: r as T[], siguiente_cursor: null };
  const o = (r ?? {}) as { items?: T[]; siguiente_cursor?: string | null };
  return { items: o.items ?? [], siguiente_cursor: o.siguiente_cursor ?? null };
}

export const api = {
  // plataforma
  salud: () => solicitar<Salud>({ metodo: 'GET', ruta: '/api/salud' }),
  yo: () => solicitar<Usuario>({ metodo: 'GET', ruta: '/api/me' }),

  // casos
  listarCasos: async (f: FiltroCasos = {}): Promise<ListaCasos> =>
    normalizarLista<CasoResumen>(
      await solicitar<unknown>({
        metodo: 'GET',
        ruta: '/api/casos',
        query: { estado: f.estado, tipo: f.tipo, q: f.q, limite: f.limite, cursor: f.cursor },
      }),
    ),
  crearCaso: (datos: NuevoCaso) => solicitar<Caso>({ metodo: 'POST', ruta: '/api/casos', cuerpo: datos }),
  obtenerCaso: (id: string) => solicitar<CasoDetalle>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}` }),
  actualizarCaso: (id: string, cambio: CambioCaso) =>
    solicitar<Caso>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}`, cuerpo: cambio }),
  procesarCaso: (id: string, dominio?: string) =>
    solicitar<{ encolado: boolean; case_id: string; dominio: string }>({
      metodo: 'POST',
      ruta: `/api/casos/${enc(id)}/procesar`,
      cuerpo: dominio ? { dominio } : {},
    }),
  correrCasoNegocio: (id: string, spec: SpecCaso) =>
    solicitar<Pick<CasoNegocio, 'id' | 'version' | 'parameters_version'> & { resultado: ResultadoMotor }>({
      metodo: 'POST',
      ruta: `/api/casos/${enc(id)}/caso-negocio`,
      cuerpo: { spec },
    }),

  // ofertas y aprobaciones
  crearOferta: (id: string, datos: NuevaOferta) =>
    solicitar<Oferta>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/ofertas`, cuerpo: datos }),
  decidirOferta: (offerId: string, datos: NuevaAprobacion) =>
    solicitar<RespuestaDecision>({
      metodo: 'POST',
      ruta: `/api/ofertas/${enc(offerId)}/aprobaciones`,
      cuerpo: datos,
    }),
  verificarOferta: (offerId: string) =>
    solicitar<VerificacionOferta>({ metodo: 'GET', ruta: `/api/ofertas/${enc(offerId)}/verificar` }),

  // cierre
  noOferta: (id: string, datos: NoOferta) =>
    solicitar<RespuestaCierre>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/no-oferta`, cuerpo: datos }),
  registrarResultado: (id: string, datos: NuevoResultado) =>
    solicitar<RespuestaCierre>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/resultado`, cuerpo: datos }),
  margenReal: (id: string) => solicitar<MargenReal>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/margen-real` }),
  aportarMargen: (id: string, area: AreaMargen, datos: Record<string, string | number>) =>
    solicitar<MargenReal>({
      metodo: 'POST',
      ruta: `/api/casos/${enc(id)}/margen-real/aportes`,
      cuerpo: { area, datos },
    }),
  conciliarMargen: (id: string) =>
    solicitar<MargenReal>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/margen-real/conciliar`, cuerpo: {} }),
  cerrarMargen: (id: string) =>
    solicitar<MargenReal>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/margen-real/cerrar`, cuerpo: {} }),

  // archivos
  subirArchivo: (id: string, archivo: File, clasificacion: Clasificacion, dominio: string) => {
    const fd = new FormData();
    fd.append('archivo', archivo);
    fd.append('clasificacion', clasificacion);
    fd.append('dominio', dominio);
    return solicitar<Archivo>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/archivos`, cuerpo: fd });
  },
  descargarArchivo: (archivoId: string) =>
    solicitar<Blob>({ metodo: 'GET', ruta: `/api/archivos/${enc(archivoId)}/descarga`, binario: true }),

  // excepciones
  listarExcepciones: async (estado: EstadoExcepcion | 'TODAS' = 'ABIERTA') =>
    normalizarLista<Excepcion>(
      await solicitar<unknown>({ metodo: 'GET', ruta: '/api/excepciones', query: { estado } }),
    ).items,
  resolverExcepcion: (id: number, datos: ResolucionExcepcion) =>
    solicitar<RespuestaResolucion>({ metodo: 'POST', ruta: `/api/excepciones/${id}/resolver`, cuerpo: datos }),

  // indicadores
  indicadores: (desde?: string, hasta?: string) =>
    solicitar<Indicadores>({ metodo: 'GET', ruta: '/api/indicadores', query: { desde, hasta } }),

  // catálogos
  listarCatalogos: async () =>
    normalizarLista<CatalogoResumen>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/catalogos' })).items,
  obtenerCatalogo: (tipo: TipoCatalogo) =>
    solicitar<CatalogoVersion>({ metodo: 'GET', ruta: `/api/catalogos/${enc(tipo)}` }),
  versionesCatalogo: async (tipo: TipoCatalogo) =>
    normalizarLista<CatalogoVersion>(
      await solicitar<unknown>({ metodo: 'GET', ruta: `/api/catalogos/${enc(tipo)}/versiones` }),
    ).items,
  crearVersionCatalogo: (tipo: TipoCatalogo, datos: NuevaVersionCatalogo) =>
    solicitar<CatalogoVersion>({ metodo: 'POST', ruta: `/api/catalogos/${enc(tipo)}`, cuerpo: datos }),
  aprobarVersionCatalogo: (tipo: TipoCatalogo, version: string) =>
    solicitar<CatalogoVersion>({
      metodo: 'POST',
      ruta: `/api/catalogos/${enc(tipo)}/${enc(version)}/aprobar`,
      cuerpo: {},
    }),

  // colaboración v1.1 (§8)
  comentarios: async (id: string) =>
    normalizarLista<Comentario>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/comentarios` })).items,
  comentar: (id: string, cuerpo: string) =>
    solicitar<Comentario>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/comentarios`, cuerpo: { cuerpo } }),
  editarComentario: (comentarioId: number, cuerpo: string) =>
    solicitar<Comentario>({ metodo: 'PATCH', ruta: `/api/comentarios/${comentarioId}`, cuerpo: { cuerpo } }),
  eliminarComentario: (comentarioId: number) =>
    solicitar<unknown>({ metodo: 'DELETE', ruta: `/api/comentarios/${comentarioId}` }),
  actividad: async (id: string) =>
    normalizarLista<ItemActividad>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/actividad` })).items,
  seguir: (id: string) => solicitar<unknown>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/seguir`, cuerpo: {} }),
  dejarDeSeguir: (id: string) => solicitar<unknown>({ metodo: 'DELETE', ruta: `/api/casos/${enc(id)}/seguir` }),
  usuarios: async (q?: string) =>
    normalizarLista<UsuarioDirectorio>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/usuarios', query: { q } })).items,
  buscar: async (q: string) =>
    normalizarLista<ResultadoBusqueda>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/buscar', query: { q } })).items,
  miTrabajo: async (): Promise<MiTrabajo> => {
    const r = (await solicitar<Partial<MiTrabajo> | null>({ metodo: 'GET', ruta: '/api/mi-trabajo' })) ?? {};
    return {
      asignados: r.asignados ?? [],
      por_aprobar: r.por_aprobar ?? [],
      margen_pendiente: r.margen_pendiente ?? [],
      excepciones: r.excepciones ?? [],
      seguidos: r.seguidos ?? [],
    };
  },
  notificaciones: async (soloNoLeidas = false): Promise<ListaNotificaciones> => {
    const r = await solicitar<unknown>({ metodo: 'GET', ruta: '/api/notificaciones', query: { no_leidas: soloNoLeidas ? 'true' : undefined } });
    const l = normalizarLista<ListaNotificaciones['items'][number]>(r);
    const noLeidas = (r as { no_leidas?: number } | null)?.no_leidas;
    return { items: l.items, no_leidas: typeof noLeidas === 'number' ? noLeidas : l.items.filter((n) => !n.leida).length };
  },
  leerNotificaciones: (ids?: number[]) =>
    solicitar<unknown>({ metodo: 'POST', ruta: '/api/notificaciones/leer', cuerpo: ids ? { ids } : {} }),

  // plataforma de preventa v1.2 (§9)
  calificacion: (id: string) => solicitar<Calificacion>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/calificacion` }),
  guardarCalificacion: (id: string, datos: Record<string, string | null>) =>
    solicitar<Calificacion>({ metodo: 'PUT', ruta: `/api/casos/${enc(id)}/calificacion`, cuerpo: datos }),
  saludCaso: (id: string) => solicitar<SaludCaso>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/salud` }),
  guardarSalud: (id: string, datos: SaludEntrada) => solicitar<SaludCaso>({ metodo: 'PUT', ruta: `/api/casos/${enc(id)}/salud`, cuerpo: datos }),
  valorMapa: async (id: string) =>
    normalizarLista<ValorMapaItem>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/valor-mapa` })).items,
  crearValorMapa: (id: string, datos: Partial<ValorMapaItem>) =>
    solicitar<ValorMapaItem>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/valor-mapa`, cuerpo: datos }),
  editarValorMapa: (id: string, item: number, datos: Partial<ValorMapaItem>) =>
    solicitar<ValorMapaItem>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}/valor-mapa/${item}`, cuerpo: datos }),
  eliminarValorMapa: (id: string, item: number) => solicitar<unknown>({ metodo: 'DELETE', ruta: `/api/casos/${enc(id)}/valor-mapa/${item}` }),
  evaluaciones: async (id: string) =>
    normalizarLista<Evaluacion>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/evaluaciones` })).items,
  crearEvaluacion: (id: string, datos: Partial<Evaluacion>) =>
    solicitar<Evaluacion>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/evaluaciones`, cuerpo: datos }),
  editarEvaluacion: (id: string, ev: number, datos: Partial<Evaluacion>) =>
    solicitar<Evaluacion>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}/evaluaciones/${ev}`, cuerpo: datos }),
  aprobarEvaluacion: (id: string, ev: number) =>
    solicitar<Evaluacion>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/evaluaciones/${ev}/aprobar`, cuerpo: {} }),
  planMutuo: async (id: string) =>
    normalizarLista<HitoPlan>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/plan-mutuo` })).items,
  crearHito: (id: string, datos: Partial<HitoPlan>) => solicitar<HitoPlan>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/plan-mutuo`, cuerpo: datos }),
  editarHito: (id: string, h: number, datos: Partial<HitoPlan>) =>
    solicitar<HitoPlan>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}/plan-mutuo/${h}`, cuerpo: datos }),
  eliminarHito: (id: string, h: number) => solicitar<unknown>({ metodo: 'DELETE', ruta: `/api/casos/${enc(id)}/plan-mutuo/${h}` }),
  actividades: async (id: string) => {
    const r = await solicitar<{ items?: ActividadPreventa[]; horas_total?: number } | null>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/actividades` });
    const items = r?.items ?? [];
    return { items, horas_total: r?.horas_total ?? items.reduce((s, a) => s + Number(a.horas || 0), 0) };
  },
  crearActividad: (id: string, datos: Partial<ActividadPreventa>) =>
    solicitar<ActividadPreventa>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/actividades`, cuerpo: datos }),
  brechas: async (id: string) => normalizarLista<Brecha>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/brechas` })).items,
  crearBrecha: (id: string, datos: Partial<Brecha>) => solicitar<Brecha>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/brechas`, cuerpo: datos }),
  editarBrecha: (id: string, b: number, datos: Partial<Brecha>) =>
    solicitar<Brecha>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}/brechas/${b}`, cuerpo: datos }),
  reuniones: async (id: string) => normalizarLista<Reunion>(await solicitar<unknown>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/reuniones` })).items,
  crearReunion: (id: string, datos: { fecha: string; asistentes?: string; notas_crudas?: string }) =>
    solicitar<Reunion>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/reuniones`, cuerpo: datos }),
  editarReunion: (id: string, r: number, datos: { fecha?: string; asistentes?: string; notas_crudas?: string }) =>
    solicitar<Reunion>({ metodo: 'PATCH', ruta: `/api/casos/${enc(id)}/reuniones/${r}`, cuerpo: datos }),
  briefReunion: (id: string, r: number) => solicitar<Reunion>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/reuniones/${r}/brief`, cuerpo: {} }),
  minutaReunion: (id: string, r: number) => solicitar<Reunion>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/reuniones/${r}/minuta`, cuerpo: {} }),
  minutaAlPlan: async (id: string, r: number, indices?: number[]) =>
    normalizarLista<HitoPlan>(
      await solicitar<unknown>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/reuniones/${r}/minuta/plan-mutuo`, cuerpo: indices ? { indices } : {} }),
    ).items,
  solicitudes: async (estado?: string) =>
    normalizarLista<Solicitud>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/solicitudes', query: { estado } })).items,
  crearSolicitud: (datos: Partial<Solicitud>) => solicitar<Solicitud>({ metodo: 'POST', ruta: '/api/solicitudes', cuerpo: datos }),
  editarSolicitud: (s: number, datos: Partial<Solicitud>) => solicitar<Solicitud>({ metodo: 'PATCH', ruta: `/api/solicitudes/${s}`, cuerpo: datos }),
  convertirSolicitud: (s: number, datos: { cliente_nombre: string; cliente_nit?: string; tema: string; tipo?: string; valor_estimado?: number; fecha_limite?: string }) =>
    solicitar<{ solicitud: Solicitud; caso: Caso }>({ metodo: 'POST', ruta: `/api/solicitudes/${s}/convertir`, cuerpo: datos }),
  biblioteca: async (f: { q?: string; tipo?: string; linea?: string } = {}) =>
    normalizarLista<ItemBiblioteca>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/biblioteca', query: f })).items,
  usarBiblioteca: (b: number, caseId?: string) =>
    solicitar<ItemBiblioteca>({ metodo: 'POST', ruta: `/api/biblioteca/${b}/usar`, query: { case_id: caseId }, cuerpo: {} }),
  equipo: () => solicitar<Equipo>({ metodo: 'GET', ruta: '/api/preventa/equipo' }),
  coach: (id: string) => solicitar<Coach>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/coach` }),
  actualizarCoach: (id: string) => solicitar<Coach>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/coach/actualizar`, cuerpo: {} }),
  brechasAgregadas: async () => normalizarLista<BrechaAgregada>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/preventa/brechas' })).items,
  valorAgregado: async (id: string): Promise<ValorAgregado> => {
    const r = (await solicitar<Partial<ValorAgregado> | null>({ metodo: 'GET', ruta: `/api/casos/${enc(id)}/valor-agregado` })) ?? {};
    return { items: r.items ?? [], escenario: r.escenario ?? null };
  },
  seleccionarValor: (id: string, items: { opcion_id: string; seleccionado: boolean }[]) =>
    solicitar<ValorAgregado>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/valor-agregado`, cuerpo: { items } }),
  recalcularValor: (id: string) => solicitar<ValorAgregado>({ metodo: 'POST', ruta: `/api/casos/${enc(id)}/valor-agregado/recalcular`, cuerpo: {} }),

  // clientes
  buscarClientes: async (q: string) =>
    normalizarLista<ClienteBusqueda>(await solicitar<unknown>({ metodo: 'GET', ruta: '/api/clientes', query: { q } }))
      .items,
};
